import { html, type TemplateResult, nothing } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { ExtBaseElement } from '../../core/ExtBaseElement';
import { openFloating, type FloatingCleanup } from '../../lib/floating';
import './ext-select.css';

export type ExtSelectSize = 'sm' | 'md' | 'lg';

export interface ExtSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  icon?: string;
}

export type ExtSelectValue = string | string[];

/**
 * <ext-select label="..." .options=${[...]} placeholder="Elegir..." multi clearable/>
 *
 * Dropdown custom. Usa portal pattern (openFloating) para que el panel no
 * quede clippeado por overflow:hidden. Single y multi select. Keyboard
 * navigation: ArrowUp/Down + Enter + Esc.
 *
 * Eventos:
 *  - ext:change - { value, option }
 *  - ext:open / ext:close
 */
@customElement('ext-select')
export class ExtSelect extends ExtBaseElement {
  @property({ type: String }) label = '';
  @property({ type: String }) placeholder = 'Seleccionar...';
  @property({ type: String }) hint = '';
  @property({ type: String, reflect: true }) error = '';
  @property({ type: String, reflect: true }) size: ExtSelectSize = 'md';
  @property({ type: String }) icon = '';
  @property({ type: String }) name = '';
  @property({ type: Boolean, reflect: true }) required = false;
  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: Boolean, reflect: true }) multi = false;
  @property({ type: Boolean, reflect: true }) clearable = false;

  @property({ attribute: false }) options: ExtSelectOption[] = [];
  @property({ attribute: false }) value: ExtSelectValue = '';

  @state() private _open = false;
  @state() private _activeIndex = -1;

  @query('.ext-select__trigger') private _triggerEl?: HTMLElement;

  private _floatingCleanup: FloatingCleanup | null = null;
  private _panelEl: HTMLDivElement | null = null;

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this._closeDropdown();
  }

  private _getSelectedValues(): string[] {
    if (Array.isArray(this.value)) return this.value;
    return this.value ? [this.value] : [];
  }

  private _getSelectedOptions(): ExtSelectOption[] {
    const sel = this._getSelectedValues();
    return this.options.filter((o) => sel.includes(o.value));
  }

  private _toggle = (): void => {
    if (this.disabled) return;
    if (this._open) this._closeDropdown();
    else this._openDropdown();
  };

  private _openDropdown(): void {
    if (this._open || !this._triggerEl) return;
    this._open = true;
    this._activeIndex = this._findFirstSelectedIndex();

    const panel = document.createElement('div');
    panel.className = 'ext-select__panel';
    panel.setAttribute('role', 'listbox');
    this._panelEl = panel;
    this._renderPanel();

    this._floatingCleanup = openFloating({
      anchor: this._triggerEl,
      panel,
      placement: 'bottom-start',
      onOutsideClick: () => this._closeDropdown(),
      onEscape: () => {
        this._closeDropdown();
        this._triggerEl?.focus();
      },
    });

    this._emit('open');
  }

  private _closeDropdown(): void {
    if (!this._open) return;
    this._open = false;
    this._activeIndex = -1;
    if (this._floatingCleanup) {
      this._floatingCleanup();
      this._floatingCleanup = null;
    }
    this._panelEl = null;
    this._emit('close');
  }

  private _findFirstSelectedIndex(): number {
    const sel = this._getSelectedValues();
    if (sel.length === 0) return this.options.findIndex((o) => !o.disabled);
    return this.options.findIndex((o) => sel.includes(o.value) && !o.disabled);
  }

  private _selectOption(opt: ExtSelectOption): void {
    if (opt.disabled) return;

    if (this.multi) {
      const current = [...this._getSelectedValues()];
      const idx = current.indexOf(opt.value);
      if (idx >= 0) current.splice(idx, 1);
      else current.push(opt.value);
      this.value = current;
      const selectedOpts = this.options.filter((o) => current.includes(o.value));
      this._emit('change', { value: current, option: selectedOpts });
      this._renderPanel();
    } else {
      this.value = opt.value;
      this._emit('change', { value: opt.value, option: opt });
      this._closeDropdown();
    }
  }

  private _clear = (ev: Event): void => {
    ev.stopPropagation();
    const newVal: ExtSelectValue = this.multi ? [] : '';
    this.value = newVal;
    this._emit('change', { value: newVal, option: this.multi ? [] : null });
    if (this._open) this._renderPanel();
  };

  private _onKeyDown = (ev: KeyboardEvent): void => {
    if (this.disabled) return;

    if (!this._open) {
      if (ev.key === 'Enter' || ev.key === ' ' || ev.key === 'ArrowDown') {
        ev.preventDefault();
        this._openDropdown();
      }
      return;
    }

    if (ev.key === 'ArrowDown') {
      ev.preventDefault();
      this._moveActive(1);
    } else if (ev.key === 'ArrowUp') {
      ev.preventDefault();
      this._moveActive(-1);
    } else if (ev.key === 'Enter') {
      ev.preventDefault();
      const opt = this.options[this._activeIndex];
      if (opt) this._selectOption(opt);
    } else if (ev.key === 'Escape') {
      ev.preventDefault();
      this._closeDropdown();
    }
  };

  private _moveActive(delta: number): void {
    if (this.options.length === 0) return;
    let i = this._activeIndex;
    for (let n = 0; n < this.options.length; n++) {
      i = (i + delta + this.options.length) % this.options.length;
      if (!this.options[i].disabled) {
        this._activeIndex = i;
        this._renderPanel();
        return;
      }
    }
  }

  private _renderPanel(): void {
    if (!this._panelEl) return;
    const panel = this._panelEl;
    panel.innerHTML = '';
    const sel = new Set(this._getSelectedValues());

    this.options.forEach((opt, idx) => {
      const item = document.createElement('div');
      item.className = 'ext-select__option';
      item.setAttribute('role', 'option');
      if (opt.disabled) item.classList.add('is-disabled');
      if (sel.has(opt.value)) item.classList.add('is-selected');
      if (idx === this._activeIndex) item.classList.add('is-active');
      item.setAttribute('aria-selected', sel.has(opt.value) ? 'true' : 'false');

      if (this.multi) {
        const cb = document.createElement('span');
        cb.className = 'ext-select__checkbox';
        if (sel.has(opt.value)) cb.classList.add('is-checked');
        item.appendChild(cb);
      }

      if (opt.icon) {
        const ic = document.createElement('ext-icon');
        ic.setAttribute('name', opt.icon);
        ic.setAttribute('size', 'sm');
        ic.setAttribute('aria-hidden', 'true');
        ic.className = 'ext-select__option-icon';
        item.appendChild(ic);
      }

      const lbl = document.createElement('span');
      lbl.className = 'ext-select__option-label';
      lbl.textContent = opt.label;
      item.appendChild(lbl);

      item.addEventListener('mousedown', (ev) => {
        ev.preventDefault();
        this._selectOption(opt);
      });
      item.addEventListener('mouseenter', () => {
        if (!opt.disabled) {
          this._activeIndex = idx;
          this._updateActiveHighlight();
        }
      });

      panel.appendChild(item);
    });

    if (this.options.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'ext-select__empty';
      empty.textContent = 'Sin opciones';
      panel.appendChild(empty);
    }
  }

  private _updateActiveHighlight(): void {
    if (!this._panelEl) return;
    const items = this._panelEl.querySelectorAll('.ext-select__option');
    items.forEach((it, i) => {
      it.classList.toggle('is-active', i === this._activeIndex);
    });
  }

  protected updated(changed: Map<string, unknown>): void {
    if (changed.has('options') && this._open) {
      this._renderPanel();
    }
    if (changed.has('value') && this._open) {
      this._renderPanel();
    }
  }

  private _renderTriggerContent(): TemplateResult {
    const selected = this._getSelectedOptions();

    if (this.multi) {
      if (selected.length === 0) {
        return html`<span class="ext-select__placeholder">${this.placeholder}</span>`;
      }
      return html`<span class="ext-select__chips">
        ${selected.map(
          (o) => html`<span class="ext-select__chip">${o.label}</span>`
        )}
      </span>`;
    }

    if (selected.length === 0) {
      return html`<span class="ext-select__placeholder">${this.placeholder}</span>`;
    }
    return html`<span class="ext-select__value">${selected[0].label}</span>`;
  }

  render(): TemplateResult {
    const hasError = !!this.error;
    const helperText = hasError ? this.error : this.hint;
    const hasValue = this._getSelectedValues().length > 0;

    return html`
      ${this.label
        ? html`<label class="ext-select__label">
            ${this.label}${this.required ? html`<span class="ext-select__req" aria-hidden="true">*</span>` : nothing}
          </label>`
        : nothing}
      <div
        class="ext-select__trigger"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded=${this._open ? 'true' : 'false'}
        aria-invalid=${hasError ? 'true' : 'false'}
        tabindex=${this.disabled ? '-1' : '0'}
        @click=${this._toggle}
        @keydown=${this._onKeyDown}
      >
        ${this.icon
          ? html`<ext-icon class="ext-select__icon" name="${this.icon}" size="sm" aria-hidden="true"></ext-icon>`
          : nothing}
        <div class="ext-select__trigger-content">${this._renderTriggerContent()}</div>
        ${this.clearable && hasValue && !this.disabled
          ? html`<button
              type="button"
              class="ext-select__clear"
              aria-label="Limpiar"
              @click=${this._clear}
              @mousedown=${(e: Event) => e.stopPropagation()}
            >
              &#x00D7;
            </button>`
          : nothing}
        <span class="ext-select__caret" aria-hidden="true">&#x25BE;</span>
      </div>
      ${helperText
        ? html`<div class="ext-select__helper ${hasError ? 'is-error' : ''}">${helperText}</div>`
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ext-select': ExtSelect;
  }
}
